import { StaticImage } from 'gatsby-plugin-image';
import { lighten, rgba } from 'polished';
import styled from 'styled-components';

import { HOMEPAGE_LOOPING_THUMB_2025 } from '@/utils';

import { generateFluidPxScale } from '../blog-2/utils';

const STATIC_IMG_PATH = '../../../static/images/home-2025';

function PlayIcon() {
  return (
    <svg
      fill="none"
      height="17"
      viewBox="0 0 15 17"
      width="15"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12.7007 7.50366C13.5359 7.9859 13.5887 9.14654 12.8579 9.71362L12.7007 9.81909L2.84131 15.5115C1.95038 16.0254 0.837402 15.3819 0.837402 14.3533L0.837402 2.96948C0.837402 2.00499 1.81587 1.37891 2.67236 1.72827L2.84131 1.81128L12.7007 7.50366Z"
        stroke="currentColor"
        stroke-width="1.32"
      />
    </svg>
  );
}

const FirstFrame = styled.div<{ $aspectRatio: string }>`
  position: absolute;
  border-radius: inherit;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  overflow: clip;
  width: 100%;
  inset: 0;
  background-color: ${({ theme }) => theme.palette.fgLoud};
  filter: brightness(0.92);

  img {
    width: 100%;
    object-fit: cover;
  }
`;

const Loop = styled.video<{ $aspectRatio: string }>`
  display: block;
  width: 100%;
  border-radius: 1px;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  position: relative;
  filter: brightness(0.92);
`;

const PlayButtonGlass = styled.span`
  position: relative;
  grid-area: 1 / -1;
  border-radius: 999px;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid transparent;

  transform: var(--indicator-transform);
  transition: transform;
  transition-duration: var(--hover-transition-duration);
  transition-timing-function: var(--hover-transition-timing-function);

  box-shadow:
    0px 72px 20px 0px rgba(0, 0, 0, 0),
    0px 46px 18px 0px rgba(0, 0, 0, 0.03),
    0px 26px 16px 0px rgba(0, 0, 0, 0.12),
    0px 12px 12px 0px rgba(0, 0, 0, 0.2),
    0px 3px 6px 0px rgba(0, 0, 0, 0.23);

  ${({ theme }) => theme.buttonLoudStyles};

  &::before,
  &::after {
    border-radius: inherit;
  }

  &::after {
    content: '';
    pointer-events: none;
    position: absolute;
    inset: 1px;
    z-index: 1;
    opacity: 1;

    background-clip: padding-box, border-box;
    background-origin: padding-box, border-box;
    transition: ${({ theme }) =>
      `opacity ${theme.animation.DURATION} ${theme.animation.EASING}`};
  }

  &:hover::after {
    opacity: 0.3;
  }

  &:active::after {
    opacity: 0.6;
  }

  border: 1px solid transparent;
  background: ${({ theme }) => {
    return `linear-gradient(
    to right,
    ${rgba(theme.backgroundColor, 0.6)},
    ${rgba(theme.backgroundColor, 0.6)}
  ),
  linear-gradient(
    45deg,
    ${theme.highlightColor},
    ${theme.colors.LIGHT_PURPLE}
  )`;
  }};
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
`;

const PlayButtonInner = styled.span`
  grid-area: 1 / -1;
  justify-self: stretch;
  position: relative;
  border-radius: 999px;
  display: grid;
  place-items: center;
  aspect-ratio: 1 / 1;
  width: ${generateFluidPxScale(66, 80)};
  width: 72px;
  background-color: ${({ theme }) => lighten(0.075, theme.palette.fg100)};
  box-shadow:
    0px 0px 1px rgba(0, 0, 0, 0.64),
    0px 1px 1px rgba(0, 0, 0, 0.48),
    0px 2px 1px rgba(0, 0, 0, 0.32),
    0px 4px 2px rgba(0, 0, 0, 0.12),
    0px 6px 2px rgba(0, 0, 0, 0.08);

  color: ${({ theme }) => theme.palette.fg1200};

  svg {
    margin-left: 5%;
    width: 32px;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }

  &::before {
    border: 1px dashed ${({ theme }) => theme.palette.fg1200};
    animation: rotate 7s linear infinite;
    animation-play-state: var(--play-indicator-state, paused);
  }

  @keyframes rotate {
    from {
      transform: rotate(-360deg);
    }
  }

  &::after {
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
  }
`;

const PlayButton = styled.div`
  position: relative;
  display: grid;
  place-content: center;
  place-items: center;
  background-color: transparent;
  border: 0;
  cursor: pointer;
  z-index: 2;
`;

export function Thumbnail({
  src,
  aspectRatio = '639 / 385',
  noPlaceholder
}: {
  aspectRatio?: string;
  noPlaceholder?: boolean;
  src?: string;
}) {
  return (
    <>
      <FirstFrame $aspectRatio={aspectRatio}>
        {/* 
          @TODO do custom image optimization and sizing and add link preload tag like below if LCP needs improvement.
          <Helmet>
            <link
              as="image"
              fetchPriority="high"
              href="/images/home/home-poster/1050.webp"
              // @ts-expect-error this is a valid link attr
              imagesizes="400px"
              imagesrcset="/images/home/home-poster/1920.webp 1920w, /images/home/home-poster/1050.webp 1050w, /images/home/home-poster/780.webp 780w, /images/home/home-poster/400.webp 400w"
              rel="preload"
            />
          </Helmet> 
        */}
        {noPlaceholder ? null : (
          <StaticImage
            alt="Image of a hex notebook"
            loading="eager"
            placeholder="blurred"
            quality={10}
            sizes="400px"
            src={`${STATIC_IMG_PATH}/first-frame.png`}
            width={710}
          />
        )}
      </FirstFrame>

      <Loop
        $aspectRatio={aspectRatio}
        autoPlay={true}
        controls={false}
        loop={true}
        muted={true}
        playsInline={true}
        poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7">
        <source src={src || HOMEPAGE_LOOPING_THUMB_2025} type="video/mp4" />
      </Loop>

      <PlayButtonGlass>
        <PlayButton>
          <PlayButtonInner>
            <PlayIcon />
          </PlayButtonInner>
        </PlayButton>
      </PlayButtonGlass>
    </>
  );
}
